/**
 * How the sync engine talks to the server. The interface lets tests (and a future Background
 * Fetch / native wrapper) swap the HTTP implementation.
 */
import { ApiError, requestRaw } from '@/api'
import { unwrap } from '@/api/envelope'
import {
  MEDIA_CONTENT_TYPE,
  SYNC_COMMANDS_PATH,
  mediaPath,
  syncCommandsResponseSchema,
  type SyncCommandsRequest,
  type SyncCommandsResponse,
} from './protocol'
import type { MediaRecord } from './db'

export interface SyncTransport {
  /** Throws ApiError on transport/HTTP failure. */
  sendCommands(req: SyncCommandsRequest): Promise<SyncCommandsResponse>
  uploadMedia(rec: MediaRecord): Promise<void>
}

/** Waits per request. Short on purpose: on a weak link a stuck request ends and the next round retries. */
export const SYNC_TIMEOUT_MS = 30_000
export const MEDIA_SIGN_TIMEOUT_MS = 15_000
export const MEDIA_PUT_TIMEOUT_MS = 45_000

/**
 * Off for the rest of this app run when S3 cannot be reached although our server can (a bucket
 * that does not allow the app's site, or an old server): photos then go through the server.
 */
let directOff = false
let directWorked = false
let directS3Failures = 0
export function resetDirectUpload(): void {
  directOff = false
  directWorked = false
  directS3Failures = 0
}
/** Whether photos still go straight to S3 in this app run. */
export const directUploadOn = () => !directOff

async function sha256Hex(blob: Blob): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return null // not a secure page (plain-http dev): use the server path
  const bytes = await new Response(blob).arrayBuffer()
  const hash = new Uint8Array(await subtle.digest('SHA-256', bytes))
  return Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The same error, its message saying which step failed (shown on the Sync screen). */
function withStep(step: string, e: unknown): unknown {
  if (e instanceof Error)
    e.message = `${step}: ${e.message}${e instanceof ApiError && e.status ? ` (${e.status})` : ''}`
  return e
}

type UploadUrl =
  { stored: true } | { stored: false; uploadUrl: string; headers?: Record<string, string> }

/**
 * Upload straight to S3 (the way farmer-onboarding does it): ask the server for a signed link,
 * PUT the photo to S3, tell the server it is there. Returns false when this phone should use the
 * server path instead.
 */
async function uploadDirect(rec: MediaRecord & { blob: Blob }): Promise<boolean> {
  if (directOff) return false
  let sha256: string | null
  try {
    sha256 = await sha256Hex(rec.blob)
  } catch (e) {
    // The bytes on this phone cannot be read (a storage fault): no upload can fix that.
    throw new Error(`Photo file unreadable on this phone: ${e instanceof Error ? e.message : e}`, {
      cause: e,
    })
  }
  if (!sha256) return false
  const mime = rec.mime || MEDIA_CONTENT_TYPE
  const info = { contentType: mime, sizeBytes: rec.blob.size, sha256 }
  let signed: UploadUrl
  try {
    const { body } = await requestRaw(`${mediaPath(rec.id)}/upload-url`, {
      method: 'POST',
      body: info,
      timeoutMs: MEDIA_SIGN_TIMEOUT_MS,
    })
    signed = unwrap(body) as UploadUrl
  } catch (e) {
    // An older server without direct uploads.
    if (e instanceof ApiError && (e.kind === 'not_found' || e.kind === 'http')) {
      directOff = true
      return false
    }
    throw withStep('upload link', e)
  }
  if (signed.stored) return true

  let put: Response
  try {
    put = await fetch(signed.uploadUrl, {
      method: 'PUT',
      body: rec.blob,
      headers: signed.headers ?? { 'Content-Type': mime },
      signal: AbortSignal.timeout(MEDIA_PUT_TIMEOUT_MS),
    })
  } catch (cause) {
    // Our server answered a moment ago, S3 did not. If S3 has never once worked in this run and
    // this keeps happening, S3 is blocked for this app (not a weak link): stop trying it. Once a
    // photo has gone straight to S3, a failure is only ever the network.
    if (!directWorked && ++directS3Failures >= 3) directOff = true
    const timedOut = cause instanceof DOMException && cause.name === 'TimeoutError'
    throw new ApiError({
      kind: timedOut ? 'timeout' : 'network',
      message: timedOut ? 'Photo upload took too long' : 'Photo upload failed (network)',
      path: 's3',
      cause,
    })
  }
  if (!put.ok) {
    // A refused signature (403, e.g. a phone clock far off) will not fix itself: use the server.
    if (put.status === 403 || put.status === 400) {
      directOff = true
      return false
    }
    throw withStep('S3', ApiError.fromResponse(put.status, null, 's3'))
  }
  directWorked = true
  directS3Failures = 0
  try {
    await requestRaw(`${mediaPath(rec.id)}/complete`, {
      method: 'POST',
      body: info,
      timeoutMs: MEDIA_SIGN_TIMEOUT_MS,
    })
  } catch (e) {
    throw withStep('record', e)
  }
  return true
}

export const httpTransport: SyncTransport = {
  async sendCommands(req) {
    const { body } = await requestRaw(SYNC_COMMANDS_PATH, {
      method: 'POST',
      body: req,
      timeoutMs: SYNC_TIMEOUT_MS,
    })
    const parsed = syncCommandsResponseSchema.safeParse(unwrap(body))
    if (!parsed.success) {
      throw new ApiError({
        kind: 'invalid_response',
        message: 'The server sent an unexpected sync reply',
        path: SYNC_COMMANDS_PATH,
        body: parsed.error.issues,
      })
    }
    return parsed.data
  },
  async uploadMedia(rec) {
    if (!rec.blob) throw new Error('Photo bytes missing')
    if (await uploadDirect(rec as MediaRecord & { blob: Blob })) return
    try {
      await requestRaw(mediaPath(rec.id), {
        method: 'PUT',
        body: rec.blob,
        headers: { 'Content-Type': rec.mime || MEDIA_CONTENT_TYPE },
        timeoutMs: MEDIA_PUT_TIMEOUT_MS,
      })
    } catch (e) {
      throw withStep('server upload', e)
    }
  },
}
